import {
  CATALOG_FILTERS_URL_STATE_VERSION,
  type CatalogCategoryNode,
  type CatalogFacet,
  type MerchantCatalogFilterUrlState,
  buildMerchantCatalogSearchHref,
} from "./catalogFilterUrlState";

const CATEGORY_PATH_SEPARATOR = " > ";
const DIRECT_FACET_FIELD_NAMES = new Set([
  "age_groups",
  "brand_name",
  "brand_slug",
  "color",
  "color_family",
  "gender",
  "is_activewear",
  "product_type",
  "raw_brand_name",
]);

export interface AnalyzerCatalogFieldSelection {
  field: {
    name: string;
  };
}

export interface AnalyzerCatalogSearchBrowseMenuNode {
  uuid: string;
  display_name: string;
  display_path: string[];
  children_nodes?: AnalyzerCatalogSearchBrowseMenuNode[];
  search_query: {
    facets?: CatalogFacet[] | null;
  };
}

export interface AnalyzerCatalogSearchBrowseMenuIndex {
  byPath: Map<string, AnalyzerCatalogSearchBrowseMenuNode>;
}

export interface AnalyzerCatalogSearchHrefArgs {
  merchantName?: string;
  catalogName: string;
  basePath?: string;
  value: string;
  selection: AnalyzerCatalogFieldSelection;
  searchFacets?: CatalogFacet[] | null;
  browseMenuIndex?: AnalyzerCatalogSearchBrowseMenuIndex | null;
}

export function buildAnalyzerCatalogSearchHref({
  basePath,
  merchantName,
  catalogName,
  value,
  selection,
  searchFacets,
  browseMenuIndex,
}: AnalyzerCatalogSearchHrefArgs): string {
  const filterState = buildAnalyzerCatalogFilterState(value, selection, {
    browseMenuIndex,
    searchFacets,
  });
  return buildMerchantCatalogSearchHref({
    basePath,
    merchantName,
    catalogName,
    q: filterState.q,
    facets: filterState.facets,
    selectedLeafPaths: filterState.selectedLeafPaths,
    categoryNavigationStack: filterState.categoryNavigationStack,
  });
}

export function buildAnalyzerCatalogFilterState(
  value: string,
  selection: AnalyzerCatalogFieldSelection,
  options: {
    browseMenuIndex?: AnalyzerCatalogSearchBrowseMenuIndex | null;
    searchFacets?: CatalogFacet[] | null;
  } = {}
): MerchantCatalogFilterUrlState {
  if (options.searchFacets && options.searchFacets.length > 0) {
    return {
      v: CATALOG_FILTERS_URL_STATE_VERSION,
      facets: options.searchFacets,
    };
  }

  if (isCategoryPathAnalyzerField(selection.field.name)) {
    const segments = parseCategoryPathAnalyzerValue(value);
    const browseMenuNode = findBrowseMenuNodeForCategoryValue(segments, options.browseMenuIndex);
    if (browseMenuNode) {
      return buildFilterStateForBrowseMenuNode(browseMenuNode);
    }

    if (segments.length > 1) {
      return {
        v: CATALOG_FILTERS_URL_STATE_VERSION,
        facets: buildCategoryPathFacets(segments),
      };
    }

    if (segments.length === 1) {
      return {
        v: CATALOG_FILTERS_URL_STATE_VERSION,
        facets: [{ name: "category_path.depth_0", values: segments }],
      };
    }
  }

  const structuredFacets = buildStructuredAttributeFacets(value, selection.field.name);
  if (structuredFacets.length > 0) {
    return {
      v: CATALOG_FILTERS_URL_STATE_VERSION,
      facets: structuredFacets,
    };
  }

  const facetName = getDirectFacetName(selection.field.name);
  const facetValues = getFacetValues(value);
  if (facetName && facetValues.length > 0) {
    return {
      v: CATALOG_FILTERS_URL_STATE_VERSION,
      facets: [{ name: facetName, values: facetValues }],
    };
  }

  return { v: CATALOG_FILTERS_URL_STATE_VERSION };
}

export function parseCategoryPathAnalyzerValue(value: string): string[] {
  const trimmedValue = stripWrappingQuotes(value.trim());
  if (!trimmedValue) return [];

  const parsed = tryParseJson(trimmedValue);
  if (Array.isArray(parsed)) {
    return parsed
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof parsed === "string" && parsed.trim()) {
    return [parsed.trim()];
  }

  if (trimmedValue.includes(CATEGORY_PATH_SEPARATOR)) {
    return trimmedValue
      .split(CATEGORY_PATH_SEPARATOR)
      .map((segment) => segment.trim())
      .filter(Boolean);
  }

  return [trimmedValue];
}

function isCategoryPathAnalyzerField(fieldName: string): boolean {
  const normalized = fieldName.replace(/\[\]/g, "");
  return normalized === "category_path" || normalized.endsWith(".category_path");
}

function getDirectFacetName(fieldName: string): string | null {
  const normalized = normalizeAnalyzerFieldName(fieldName);
  if (DIRECT_FACET_FIELD_NAMES.has(normalized)) return normalized;
  if (normalized.startsWith("attribute_facets.")) return normalized;
  return null;
}

function normalizeAnalyzerFieldName(fieldName: string): string {
  return fieldName.replace(/\[\]/g, "").replace(/^enrichment\./, "");
}

function getFacetValues(value: string): string[] {
  const parsed = tryParseJson(value);
  const rawValues = Array.isArray(parsed) ? parsed : [parsed ?? value];
  return rawValues
    .map((item) => {
      if (typeof item === "string") return stripWrappingQuotes(item.trim());
      if (typeof item === "number" || typeof item === "boolean") return String(item);
      return null;
    })
    .filter((item): item is string => !!item);
}

function buildStructuredAttributeFacets(value: string, fieldName: string): CatalogFacet[] {
  const normalized = normalizeAnalyzerFieldName(fieldName);
  if (normalized === "attribute_handles") {
    const handleMap = parseStringListMap(value);
    if (!handleMap) return [];

    return Object.entries(handleMap)
      .map(([name, values]) => ({ name, values: values.filter(Boolean) }))
      .filter((facet) => facet.values.length > 0);
  }

  if (normalized === "structured_attributes") {
    const items = parseStructuredAttributeItems(value);
    if (!items) return [];

    return items
      .map((item) => ({
        name: item.handle,
        values: item.values
          .map((structuredValue) => structuredValue.handle)
          .filter((handle): handle is string => !!handle),
      }))
      .filter((facet): facet is CatalogFacet => !!facet.name && facet.values.length > 0);
  }

  return [];
}

function parseStringListMap(value: string): Record<string, string[]> | null {
  const parsed = tryParseJson(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

  const output: Record<string, string[]> = {};
  for (const [key, rawValues] of Object.entries(parsed)) {
    if (!Array.isArray(rawValues)) return null;
    output[key] = rawValues.filter((item): item is string => typeof item === "string");
  }
  return output;
}

interface StructuredAttributeItem {
  handle?: string;
  values: Array<{ handle?: string }>;
}

function parseStructuredAttributeItems(value: string): StructuredAttributeItem[] | null {
  const parsed = tryParseJson(value);
  const rawItems = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
  const items = rawItems
    .map((item): StructuredAttributeItem | null => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return null;
      const record = item as Record<string, unknown>;
      return {
        handle: typeof record.handle === "string" ? record.handle : undefined,
        values: Array.isArray(record.values)
          ? record.values
              .filter(
                (structuredValue): structuredValue is Record<string, unknown> =>
                  !!structuredValue &&
                  typeof structuredValue === "object" &&
                  !Array.isArray(structuredValue)
              )
              .map((structuredValue) => ({
                handle:
                  typeof structuredValue.handle === "string" ? structuredValue.handle : undefined,
              }))
          : [],
      };
    })
    .filter((item): item is StructuredAttributeItem => item !== null);

  return items.length > 0 ? items : null;
}

function buildCategoryPathFacets(segments: string[]): CatalogFacet[] {
  return segments.map((segment, depth) => ({
    name: `category_path.depth_${depth}`,
    values: [segment],
  }));
}

function buildFilterStateForBrowseMenuNode(
  node: AnalyzerCatalogSearchBrowseMenuNode
): MerchantCatalogFilterUrlState {
  const facets = (node.search_query.facets ?? []).map((facet) => ({
    name: facet.name,
    values: [...facet.values],
  }));
  const isLeaf = (node.children_nodes?.length ?? 0) === 0;
  const navigationPath = isLeaf ? node.display_path.slice(0, -1) : node.display_path;

  return {
    v: CATALOG_FILTERS_URL_STATE_VERSION,
    facets,
    selectedLeafPaths: isLeaf ? [formatDisplayPath(node.display_path)] : undefined,
    categoryNavigationStack: buildCategoryNavigationStack(navigationPath),
  };
}

function buildCategoryNavigationStack(path: string[]): CatalogCategoryNode[] | undefined {
  if (path.length === 0) return undefined;

  return path.map((name, index) => ({
    name,
    path: formatDisplayPath(path.slice(0, index + 1)),
  }));
}

function findBrowseMenuNodeForCategoryValue(
  segments: string[],
  browseMenuIndex?: AnalyzerCatalogSearchBrowseMenuIndex | null
): AnalyzerCatalogSearchBrowseMenuNode | null {
  if (!browseMenuIndex || segments.length === 0) return null;

  if (segments.length > 1) {
    const exactPathMatch = browseMenuIndex.byPath.get(formatDisplayPath(segments));
    if (exactPathMatch) return exactPathMatch;
  }

  const targetLabel = normalizeCategoryLabel(segments[segments.length - 1]);
  const matches = Array.from(browseMenuIndex.byPath.values()).filter(
    (node) => normalizeCategoryLabel(node.display_name) === targetLabel
  );

  if (matches.length !== 1) return null;
  return matches[0];
}

function formatDisplayPath(path: string[]): string {
  return path
    .map((part) => part.trim())
    .filter(Boolean)
    .join(CATEGORY_PATH_SEPARATOR);
}

function normalizeCategoryLabel(value: string): string {
  return value.trim().toLowerCase();
}

function stripWrappingQuotes(value: string): string {
  return value.replace(/^"(.*)"$/, "$1");
}

function tryParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
